import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import isEqual from 'lodash/isEqual';
import {
  EntertainmentVideoItem,
  EntertainmentVideoItemBlank,
  FullEntertainmentVideo
} from 'modules/entertainment-videos/types';

export interface FormState {
  formValues: FormValues,
  isModified: boolean,
  setFieldValue: <K extends keyof FormValues>(
    fieldName: K,
    newValue: FormValues[K]
  ) => void
}

export interface FormValues {
  videoUrl: string | null,
  thumbnailUrl: string | null,
  isProductReview: boolean,
  audioType: string | null,
  genders: Array<string>,
  tags: Array<string>,
  publishedCountries: Array<string>,
  publishedFrom: string | null,
  publishedUntil: string | null,
  items: Array<EntertainmentVideoItemBlank | EntertainmentVideoItem>
}

const defaultValue: FormState = {
  formValues: {
    videoUrl: null,
    thumbnailUrl: null,
    isProductReview: false,
    audioType: null,
    genders: [],
    tags: [],
    publishedCountries: [],
    publishedFrom: null,
    publishedUntil: null,
    items: []
  },
  isModified: false,
  setFieldValue: () => {}
};

const FormContext = createContext(defaultValue);

export function useFormContext() {
  return useContext(FormContext);
}

function generateFormValuesFromFullEntertainmentValues(
  fullEntertainmentVideo: FullEntertainmentVideo
): FormValues {
  return {
    videoUrl: fullEntertainmentVideo.media?.url ?? null,
    thumbnailUrl: fullEntertainmentVideo.media?.thumbnailUrl ?? null,
    isProductReview: fullEntertainmentVideo.entertainmentVideo.isProductReview ?? false,
    audioType: fullEntertainmentVideo.entertainmentVideo.audioType ?? null,
    genders: fullEntertainmentVideo.entertainmentVideo.genders ?? [],
    tags: fullEntertainmentVideo.entertainmentVideo.tags ?? [],
    publishedCountries: (
      fullEntertainmentVideo.entertainmentVideo.publishedCountries ?? []
    )
      .slice()
      .sort(),
    publishedFrom:
      fullEntertainmentVideo.entertainmentVideo.publishedFrom ?? null,
    publishedUntil:
      fullEntertainmentVideo.entertainmentVideo.publishedUntil ?? null,
    items: fullEntertainmentVideo.items ?? []
  };
}

export function FormProvider({
  fullEntertainmentVideo,
  children
}: PropsWithChildren<{ fullEntertainmentVideo: FullEntertainmentVideo }>) {
  const initialFormValuesRef = useRef(
    generateFormValuesFromFullEntertainmentValues(fullEntertainmentVideo)
  );
  const initialFormValues = initialFormValuesRef.current;
  const [formValues, setFormValues] = useState(initialFormValues);

  /**
   * Reset form once video has been saved
   */
  useEffect(() => {
    const newFormValues = generateFormValuesFromFullEntertainmentValues(
      fullEntertainmentVideo
    );

    initialFormValuesRef.current = newFormValues;
    setFormValues(newFormValues);
  }, [fullEntertainmentVideo]);

  const setFieldValue = useCallback(
    <K extends keyof FormValues>(fieldName: K, newValue: FormValues[K]) => {
      setFormValues(currentFormValues => ({
        ...currentFormValues,
        [fieldName]: newValue
      }));
    },
    []
  );

  function isVideoUrlChanged(): boolean {
    return initialFormValues.videoUrl !== formValues.videoUrl;
  }

  function isThumbnailUrlChanged(): boolean {
    return initialFormValues.thumbnailUrl !== formValues.thumbnailUrl;
  }

  function isProductReviewChanged(): boolean {
    return initialFormValues.isProductReview !== formValues.isProductReview;
  }

  function isAudioTypeChanged(): boolean {
    return initialFormValues.audioType !== formValues.audioType;
  }

  function isGendersChanged(): boolean {
    return !isEqual(initialFormValues.genders, formValues.genders);
  }

  function isTagsChanged(): boolean {
    return !isEqual(initialFormValues.tags, formValues.tags);
  }

  function isPublishedCountriesChanged(): boolean {
    return !isEqual(
      initialFormValues.publishedCountries,
      formValues.publishedCountries
    );
  }

  function isPublishedFromChanged(): boolean {
    return initialFormValues.publishedFrom !== formValues.publishedFrom;
  }

  function isPublishedUntilChanged(): boolean {
    return initialFormValues.publishedUntil !== formValues.publishedUntil;
  }

  function isItemsChanged(): boolean {
    if (initialFormValues.items.length !== formValues.items.length) {
      return true;
    }

    return !isEqual(
      initialFormValues.items.map(item => item.sku).sort(),
      formValues.items.map(item => item.sku).sort()
    );
  }

  const isModified = useMemo((): boolean => (
    isVideoUrlChanged() ||
    isThumbnailUrlChanged() ||
    isProductReviewChanged() ||
    isAudioTypeChanged() ||
    isGendersChanged() ||
    isTagsChanged() ||
    isPublishedCountriesChanged() ||
    isPublishedFromChanged() ||
    isPublishedUntilChanged() ||
    isItemsChanged()
  ), [formValues]);

  return (
    <FormContext.Provider
      value={{
        formValues,
        isModified,
        setFieldValue
      }}
    >
      {children}
    </FormContext.Provider>
  );
}
